import type { TeamOfficeGetResponse } from "@bezoned/shared/types/api/team/team-office-get.types.js";
import { useMemo } from "react";

import { GeneralMeeting } from "../office-elements/general-meeting.js";
import { GeneralTable } from "../office-elements/general-table.js";
import { buildModelUrl } from "../office/office.service.js";
import { useOffice } from "../office/use-office.js";

interface TablePairing {
  table: TeamOfficeGetResponse["tables"][number];
  displayName: string;
}

interface MeetingRoomPairing {
  presetMeetingRoom: TeamOfficeGetResponse["preset"]["manifest"]["meetingRooms"][number];
  meetingRoom: TeamOfficeGetResponse["meetingRooms"][number];
}

const buildTablePairingMap = ({
  manifestTables,
  tables,
}: {
  manifestTables: TeamOfficeGetResponse["preset"]["manifest"]["tables"];
  tables: TeamOfficeGetResponse["tables"];
}) => {
  const tableByPresetId = new Map(
    tables.map((t) => [t.presetTableId, t] as const)
  );
  const map = new Map<string, TablePairing>();

  for (const manifestTable of manifestTables) {
    const responseTable = tableByPresetId.get(manifestTable.id);
    if (!responseTable) {
      continue;
    }

    map.set(manifestTable.id, {
      displayName: responseTable.name ?? manifestTable.defaultName,
      table: responseTable,
    });
  }

  return map;
};

const buildMeetingRoomPairings = ({
  manifestMeetingRooms,
  meetingRooms,
}: {
  manifestMeetingRooms: TeamOfficeGetResponse["preset"]["manifest"]["meetingRooms"];
  meetingRooms: TeamOfficeGetResponse["meetingRooms"];
}) => {
  const pairings: MeetingRoomPairing[] = [];

  for (const [index, presetMeetingRoom] of manifestMeetingRooms.entries()) {
    const meetingRoom = meetingRooms.at(index);
    if (!meetingRoom) {
      break;
    }

    pairings.push({
      meetingRoom,
      presetMeetingRoom,
    });
  }

  return pairings;
};

export const DynamicFurnitureRenderer = () => {
  const { preset, tables, meetingRooms } = useOffice();

  const tablePairingMap = useMemo(
    () =>
      buildTablePairingMap({
        manifestTables: preset.manifest.tables,
        tables,
      }),
    [preset.manifest.tables, tables]
  );

  const meetingRoomPairings = useMemo(
    () =>
      buildMeetingRoomPairings({
        manifestMeetingRooms: preset.manifest.meetingRooms,
        meetingRooms,
      }),
    [preset.manifest.meetingRooms, meetingRooms]
  );

  return (
    <>
      {preset.manifest.tables
        .map((item) => {
          const pairing = tablePairingMap.get(item.id);
          if (!pairing) {
            return null;
          }

          const tableProps = {
            castShadow: true,
            channelUuid: pairing.table.channelUuid,
            displayName: pairing.displayName,
            modelPath: buildModelUrl(preset.modelsDirectory, item.model),
            receiveShadow: true,
            tableId: pairing.table.id,
          };

          switch (item.type) {
            case "BigTable":
            case "SmallTable":
            case "Sofa": {
              return <GeneralTable key={item.id} {...item} {...tableProps} />;
            }

            default: {
              return null;
            }
          }
        })
        .filter(Boolean)}

      {meetingRoomPairings.map(({ presetMeetingRoom, meetingRoom }) => {
        const meetingRoomProps = {
          castShadow: true,
          meetingRoomId: meetingRoom.id,
          meetingRoomName: presetMeetingRoom.defaultName,
          modelPath: buildModelUrl(
            preset.modelsDirectory,
            presetMeetingRoom.model
          ),
          receiveShadow: true,
        };

        return (
          <GeneralMeeting
            key={meetingRoom.id}
            {...presetMeetingRoom}
            {...meetingRoomProps}
          />
        );
      })}
    </>
  );
};
